import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Ban } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

interface CancelEstimateDialogProps {
    open: boolean;
    onClose: () => void;
    estimateId: string;
    /** Shown in the dialog body for context (e.g. "Estimate #E-1042"). */
    estimateLabel?: string | null;
    /** Current notes — the cancellation reason is appended so history is kept. */
    existingNotes?: string | null;
    /** Called after a successful cancel so the caller can refresh/navigate. */
    onCancelled?: () => void;
}

/**
 * Merge the required cancellation reason into the estimate's notes without
 * clobbering what's already there — dead quotes need a clean exit that keeps
 * history (FEATURE-TODOS "Cancel quote button").
 */
export function buildCancelledNotes(existingNotes: string | null | undefined, reason: string): string {
    const note = `Cancelled: ${reason.trim()}`;
    const existing = existingNotes?.trim();
    return existing ? `${existing}\n\n${note}` : note;
}

/**
 * Confirmation dialog for cancelling a quote (EST-1). The server already
 * supports the transition — PATCH /api/estimates/:id with status "cancelled";
 * the reason note is required and appended to the estimate's notes.
 */
export function CancelEstimateDialog({
    open,
    onClose,
    estimateId,
    estimateLabel,
    existingNotes,
    onCancelled,
}: CancelEstimateDialogProps) {
    const qc = useQueryClient();
    const [reason, setReason] = useState("");

    const cancelEstimate = useMutation({
        mutationFn: () =>
            api.patch(`/api/estimates/${estimateId}`, {
                status: "cancelled",
                notes: buildCancelledNotes(existingNotes, reason),
            }),
        onSuccess: () => {
            toast.success("Quote cancelled");
            qc.invalidateQueries({ queryKey: ["estimate", estimateId] });
            qc.invalidateQueries({ queryKey: ["commercial"] });
            setReason("");
            onCancelled?.();
            onClose();
        },
        onError: (err: any) => {
            const msg = err?.body?.message ?? err?.body?.error ?? err?.message ?? "unknown error";
            toast.error(`Cancel failed: ${msg}`);
        },
    });

    const canSubmit = reason.trim().length > 0 && !cancelEstimate.isPending;

    return (
        <Dialog open={open} onClose={onClose} title="Cancel Quote">
            <div className="space-y-4">
                <p className="text-sm text-foreground/80">
                    Cancel{" "}
                    {estimateLabel ? <span className="font-mono font-medium">{estimateLabel}</span> : "this quote"}? It
                    keeps its history but can no longer be sent or approved. This can&apos;t be undone.
                </p>
                <div className="space-y-1.5">
                    <Label htmlFor="cancel-quote-reason">Reason *</Label>
                    <Textarea
                        id="cancel-quote-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is this quote being cancelled?"
                        rows={3}
                    />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={cancelEstimate.isPending}>
                        Keep Quote
                    </Button>
                    <Button
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={!canSubmit}
                        onClick={() => cancelEstimate.mutate()}
                    >
                        {cancelEstimate.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <Ban className="h-4 w-4 mr-1" />
                        )}
                        Cancel Quote
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
